import { Box, Stack, Typography } from '@mui/material';

import { FormAwareRadioGroup } from '@/components/form/formAwareInputs/FormAwareRadioGroup';
import { Card } from '@/components/layout/Card/Card';
import { useFormContext } from '@/components/react-hook-form';
import { useTaxStatusAssetLocationOptions } from '@/modules/entities/TaxStatusSubform/hooks/useTaxStatusAssetLocationOptions';
import { EntityType } from '@/modules/entities/types/EntityType';

import {
  CREATE_ENTITY_SHORT_FORM_NAMESPACE,
  EntityShortFormShape,
} from '../CreateEntityShortForm.types';
import { useDefaultEstateInclusionStatus } from '../hooks/useDefaultEstateInclusionStatus';

export interface AssetLocationProps {
  entityType: EntityType;
}

// Note: There is a similar asset location UI in the TaxStatusSubform and in
// testamentaryEntities/CreateTestamentaryEntityModal/CreateTestamentaryEntityShortForm.tsx.
export function AssetLocation({ entityType }: AssetLocationProps) {
  const { control } = useFormContext<EntityShortFormShape>();

  const { assetLocationOptions: assetLocationOptionsV2 } =
    useTaxStatusAssetLocationOptions({
      fieldNamePrefix: CREATE_ENTITY_SHORT_FORM_NAMESPACE,
    });

  useDefaultEstateInclusionStatus({ entityType });

  return (
    <Card
      variant="filled"
      sx={{
        pb: 4,
      }}
    >
      <Stack spacing={2}>
        <Typography variant="title-s">Asset location</Typography>
        <Stack direction="row" width="100%">
          <Stack justifyContent="flex-end" spacing={1}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box width="60px" />
              <Typography width="60px" variant="body-xs">
                In estate
              </Typography>
              <Typography width="86px" variant="body-xs">
                Out of estate
              </Typography>
            </Stack>
            {assetLocationOptionsV2.map(({ label, fieldName, options }) => (
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                key={label}
              >
                <Typography width="60px" variant="body-xs">
                  {label}
                </Typography>
                <FormAwareRadioGroup<EntityShortFormShape>
                  sx={{
                    flexFlow: 'nowrap',
                  }}
                  labelSx={{
                    width: '84px', // 60px width + 24px padding
                  }}
                  row={true}
                  options={options}
                  fieldName={fieldName}
                  control={control}
                />
              </Stack>
            ))}
            <Stack direction="row" spacing={2} alignItems="center" key="state">
              <Typography width="60px" variant="body-xs">
                State
              </Typography>
              <FormAwareRadioGroup<EntityShortFormShape>
                sx={{
                  flexFlow: 'nowrap',
                }}
                labelSx={{
                  width: '84px', // 60px width + 24px padding
                }}
                row={true}
                options={[
                  { label: '', value: 'in-estate' },
                  { label: '', value: 'out-of-estate' },
                ]}
                fieldName={`${CREATE_ENTITY_SHORT_FORM_NAMESPACE}.stateInEstateStatus`}
                control={control}
              />
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </Card>
  );
}
